import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Webhook, Loader2, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function StatusBadge({ valid }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${
      valid
        ? "bg-[#114232]/8 text-[#114232] border-[#114232]/15"
        : "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25"
    }`}>
      {valid ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
      {valid ? "Signed" : "Unsigned/Invalid"}
    </span>
  );
}

export default function Webhooks() {
  const [data, setData] = useState({ items: [], total: 0, stats: { signed_ok: 0, signature_invalid: 0 } });
  const [filters, setFilters] = useState({ provider: "__all__", kind: "__all__", validity: "__all__" });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filters.provider !== "__all__") params.set("provider", filters.provider);
      if (filters.kind !== "__all__") params.set("kind", filters.kind);
      if (filters.validity === "valid") params.set("signature_valid", "true");
      if (filters.validity === "invalid") params.set("signature_valid", "false");
      const r = await api.get(`/admin/webhooks?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="webhooks-page">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Webhook className="w-6 h-6 text-[#c15337]" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#114232]">Webhook deliveries</h1>
            <p className="text-xs text-[#5c6360]">Inbound provider webhooks — verify signatures, replay troubleshooting</p>
          </div>
        </div>
        <Button onClick={load} variant="outline" data-testid="webhooks-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="webhooks-stats">
        <div className="bg-white border border-[#114232]/10 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Total</div>
          <div className="font-heading text-2xl font-bold text-[#114232]">{data.total}</div>
        </div>
        <div className="bg-white border border-[#114232]/10 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Signed OK</div>
          <div className="font-heading text-2xl font-bold text-[#114232]" data-testid="stat-signed-ok">
            {data.stats.signed_ok}
          </div>
        </div>
        <div className="bg-white border border-[#c15337]/20 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Invalid / unsigned</div>
          <div className="font-heading text-2xl font-bold text-[#8b3a23]" data-testid="stat-signed-bad">
            {data.stats.signature_invalid}
          </div>
        </div>
        <div className="bg-white border border-[#114232]/10 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Currently shown</div>
          <div className="font-heading text-2xl font-bold text-[#114232]">{data.items.length}</div>
        </div>
      </div>

      <div className="bg-white border border-[#114232]/10 rounded-md p-3 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Provider</Label>
          <Select value={filters.provider} onValueChange={(v) => setFilters({ ...filters, provider: v })}>
            <SelectTrigger data-testid="webhooks-filter-provider" className="min-w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All providers</SelectItem>
              <SelectItem value="mpesa">M-Pesa</SelectItem>
              <SelectItem value="capitalpay">CapitalPay</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Kind</Label>
          <Select value={filters.kind} onValueChange={(v) => setFilters({ ...filters, kind: v })}>
            <SelectTrigger data-testid="webhooks-filter-kind" className="min-w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All kinds</SelectItem>
              <SelectItem value="payment">payment</SelectItem>
              <SelectItem value="reversal">reversal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Signature</Label>
          <Select value={filters.validity} onValueChange={(v) => setFilters({ ...filters, validity: v })}>
            <SelectTrigger data-testid="webhooks-filter-validity" className="min-w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="valid">Signed OK</SelectItem>
              <SelectItem value="invalid">Invalid / unsigned</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[#114232]/10 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="webhooks-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Created</th>
              <th className="text-left font-medium py-3 px-5">Provider</th>
              <th className="text-left font-medium py-3 px-5">Kind</th>
              <th className="text-left font-medium py-3 px-5">Signature</th>
              <th className="text-left font-medium py-3 px-5">Reference</th>
              <th className="text-right font-medium py-3 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center">
                <Loader2 className="w-5 h-5 animate-spin inline text-[#114232]" />
              </td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-[#5c6360]" data-testid="webhooks-empty">
                No webhook deliveries match those filters.
              </td></tr>
            )}
            {!loading && data.items.map((w) => {
              const ref = (w.raw_payload || {}).reference
                || (w.raw_payload || {}).TransID
                || (w.raw_payload || {}).BillRefNumber
                || "—";
              return (
                <React.Fragment key={w.id}>
                  <tr className="border-t border-[#114232]/10" data-testid={`webhook-row-${w.id}`}>
                    <td className="py-3 px-5 text-xs font-mono text-[#5c6360]">
                      {w.created_at?.slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="py-3 px-5 capitalize">{w.provider}</td>
                    <td className="py-3 px-5 text-xs uppercase tracking-wider">{w.kind || "—"}</td>
                    <td className="py-3 px-5"><StatusBadge valid={w.signature_valid} /></td>
                    <td className="py-3 px-5 font-mono text-xs">{String(ref).slice(0, 28)}</td>
                    <td className="py-3 px-5 text-right">
                      <Button variant="ghost" size="sm"
                                onClick={() => setExpanded(expanded === w.id ? null : w.id)}
                                data-testid={`webhook-expand-${w.id}`}>
                        {expanded === w.id ? "Hide" : "Inspect"}
                      </Button>
                    </td>
                  </tr>
                  {expanded === w.id && (
                    <tr className="border-t border-[#114232]/10 bg-[#f4f2eb]/40">
                      <td colSpan={6} className="py-3 px-5">
                        <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-[#114232]/5 p-3 rounded border border-[#114232]/10"
                              data-testid={`webhook-detail-${w.id}`}>
                          {JSON.stringify(w, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
